import { useAuth } from '@/auth/AuthContext';
import { PATHS } from '@/constants';
import DOHALogo from '@/images/DOHA_Logo.svg';
import { isExternalLink } from '@/utils/linkUtils';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../Icon/Icon';
import { colorPalette } from '../../Theme/colors';
import styles from './Header.module.scss';

const pages = [
  { name: 'Home', path: PATHS.APPLICANT.DASHBOARD },
  { name: 'My Applications', path: PATHS.APPLICANT.MY_APPLICATIONS },
  {
    name: 'Help and Support',
    path: `${import.meta.env.VITE_TIS_NATIONAL_BASE_URL}/Our-services/Free-Translating-Service/About`,
  },
];

const Header: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const { logout, isAuthenticated } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();

  const isShowMenuItems =
    isAuthenticated && !(location.pathname === PATHS.AUTH.SIGNUP);

  const handleToggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleNavigate = (path: string) => {
    setDrawerOpen(false);

    if (isExternalLink(path)) {
      window.open(path); // Open in a new tab
    } else {
      window.location.href = path; // Open in the current tab
    }
  };

  const handleLogout = async () => {
    setDrawerOpen(false);
    await logout();
  };

  return (
    <AppBar position="static" className={styles.header}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Mobile Menu Button */}
          {isShowMenuItems && (
            <Box
              sx={{ display: { xs: 'flex', md: 'none' } }}
              className={styles.mobileMenuButton}
            >
              <IconButton onClick={handleToggleDrawer} color="inherit">
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Logo */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            <a href="/" style={{ display: 'inline-block' }}>
              <img src={DOHALogo} alt="DOHALogo" className={styles.logoImage} />
            </a>
          </Box>

          {/* Desktop Menu */}
          {isShowMenuItems && (
            <Box
              sx={{ display: { xs: 'none', md: 'flex' } }}
              className={styles.desktopMenu}
            >
              {pages.map((page) => (
                <Button
                  key={page.name}
                  className={`${styles.menuItemText} ${
                    location.pathname === page.path ? styles.active : ''
                  }`}
                  onClick={() => handleNavigate(page.path)}
                >
                  {page.name}
                </Button>
              ))}
              {/* ✅ Log Out button on desktop */}
              <Button className={styles.menuItemText} onClick={handleLogout}>
                Log Out
              </Button>
            </Box>
          )}

          {/* Desktop User Menu */}
          {isShowMenuItems && (
            <Box className={styles.userMenu}>
              <IconButton
                onClick={() => navigate(PATHS.APPLICANT.MY_ACCOUNT)}
                className={styles.profileButton}
              >
                <Icon name="profile" className={styles.profileIcon} size={28} />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      {isShowMenuItems && (
        <Drawer anchor="left" open={drawerOpen} onClose={handleToggleDrawer}>
          <Box sx={{ width: '100vw', height: '100vh', p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: colorPalette.common.black, fontSize: '20px' }}
              >
                Menu
              </Typography>
              <IconButton onClick={handleToggleDrawer}>
                <CloseIcon sx={{ color: colorPalette.common.black }} />
              </IconButton>
            </Box>
            <Divider sx={{ borderColor: colorPalette.common.black }} />
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {pages.map((page) => (
                <Button
                  key={page.name}
                  onClick={() => {
                    handleNavigate(page.path);
                  }}
                  className={styles.linkButton}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    color: colorPalette.common.black,
                  }}
                >
                  {page.name}
                </Button>
              ))}
              {/* ✅ Log Out button in mobile drawer */}
              <Button
                onClick={handleLogout}
                className={styles.linkButton}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: colorPalette.common.black,
                }}
              >
                Log Out
              </Button>
            </Box>
          </Box>
        </Drawer>
      )}
    </AppBar>
  );
};

export default Header;
